import { ReactNode, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled, useTheme } from '@mui/material/styles';
import { Role } from '@lov/shared';
import { Navbar } from '../Navbar';
import { Sidebar } from '../Sidebar';
import { PageTransition } from '../PageTransition';
import { BottomNavigation, vendorBottomNavItems } from '../navigation';
import { useAuth } from '../../contexts/AuthContext';
import { MobileDrawerProvider, useMobileDrawer } from '../../contexts/MobileDrawerContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { CustomerLayout } from './CustomerLayout';
import { SubscriptionExpiryBanner } from '../../features/vendor/subscription/components/SubscriptionExpiryBanner';

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

interface MainLayoutProps {
  children: ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | false;
  hideSidebar?: boolean;
}

const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  position: 'relative',
}));

const LayoutContainer = styled(Box)(() => ({
  display: 'flex',
}));

const ContentWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'hasSidebar' && prop !== 'sidebarCollapsed' && prop !== 'hasBottomNav',
})<{ hasSidebar?: boolean; sidebarCollapsed?: boolean; hasBottomNav?: boolean }>(
  ({ theme, hasSidebar, sidebarCollapsed, hasBottomNav }) => ({
    flex: 1,
    position: 'relative',
    zIndex: 1,
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(8),
    minHeight: 'calc(100vh - 72px)',
    width: hasSidebar ? `calc(100% - ${sidebarCollapsed ? 56 : 240}px)` : '100%',
    transition: 'width 0.2s ease',

    [theme.breakpoints.down('lg')]: {
      width: '100%',
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(6),
    },

    // Mobile vendor layout: contained scroll + bottom nav padding
    ...(hasBottomNav && {
      [theme.breakpoints.down('md')]: {
        height: 'calc(100vh - 72px)',
        minHeight: 'unset',
        overflowY: 'auto',
        overscrollBehaviorY: 'contain',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: 'calc(64px + env(safe-area-inset-bottom) + 16px)',
      },
    }),
  })
);

const SidebarWrapper = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}));

/**
 * Inner layout for Vendor/Admin with access to MobileDrawerContext
 */
function VendorAdminLayout({
  children,
  maxWidth,
  hideSidebar,
}: {
  children: ReactNode;
  maxWidth: MainLayoutProps['maxWidth'];
  hideSidebar: boolean;
}) {
  const { isAuthenticated, user } = useAuth();
  const isMobile = useIsMobile();
  const location = useLocation();
  const { toggleDrawer } = useMobileDrawer();
  const theme = useTheme();
  const isNarrowDesktop = useMediaQuery(theme.breakpoints.between('lg', 1280));

  // Sidebar collapse state with localStorage persistence
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      if (stored !== null) return stored === 'true';
    } catch {
      /* private browsing fallback */
    }
    return false;
  });

  // Auto-collapse on narrow desktop (1200-1280px) if user hasn't set a preference
  useEffect(() => {
    try {
      if (localStorage.getItem(SIDEBAR_COLLAPSED_KEY) !== null) return;
    } catch {
      /* private browsing fallback */
    }
    setSidebarCollapsed(isNarrowDesktop);
  }, [isNarrowDesktop]);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {
        /* private browsing fallback */
      }
      return next;
    });
  }, []);

  // Ctrl+B / Cmd+B keyboard shortcut to toggle sidebar
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        const tag = (e.target as HTMLElement)?.tagName;
        const isEditable =
          tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable;
        if (isEditable) return;
        e.preventDefault();
        toggleSidebar();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  const showSidebar = isAuthenticated && !hideSidebar;
  const isVendorOrStaff = user?.role === Role.VENDOR || user?.role === Role.VENDOR_STAFF;
  const showBottomNav = isMobile && isVendorOrStaff;

  return (
    <PageWrapper>
      <Navbar />
      {isVendorOrStaff && <SubscriptionExpiryBanner />}
      <LayoutContainer>
        {showSidebar && (
          <SidebarWrapper>
            <Sidebar collapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} />
          </SidebarWrapper>
        )}
        <ContentWrapper
          hasSidebar={showSidebar}
          sidebarCollapsed={sidebarCollapsed}
          hasBottomNav={showBottomNav}
        >
          <Container maxWidth={maxWidth} sx={{ px: { xs: 2, sm: 3 } }}>
            <PageTransition pageKey={location.pathname}>{children}</PageTransition>
          </Container>
        </ContentWrapper>
      </LayoutContainer>
      {showBottomNav && (
        <BottomNavigation items={vendorBottomNavItems} onMorePress={toggleDrawer} />
      )}
    </PageWrapper>
  );
}

/**
 * Main layout with role-based routing
 *
 * - CUSTOMER: Uses CustomerLayout (no sidebar, bottom nav on mobile)
 * - VENDOR/VENDOR_STAFF/SUPER_ADMIN: Uses sidebar layout with hamburger drawer on mobile
 */
export function MainLayout({ children, maxWidth = 'lg', hideSidebar = false }: MainLayoutProps) {
  const { isAuthenticated, user } = useAuth();

  // Customer gets a different layout (no sidebar, bottom nav)
  if (isAuthenticated && user?.role === Role.CUSTOMER) {
    return <CustomerLayout maxWidth={maxWidth}>{children}</CustomerLayout>;
  }

  // Vendor/Admin get sidebar layout wrapped in MobileDrawerProvider
  return (
    <MobileDrawerProvider>
      <VendorAdminLayout maxWidth={maxWidth} hideSidebar={hideSidebar}>
        {children}
      </VendorAdminLayout>
    </MobileDrawerProvider>
  );
}
